import { useContext } from 'react';
import { PageContext } from 'src/context/PageContext';
import { useResidentSession } from 'src/components/Auth/useResidentSession';
import { usePreferencesQuery } from 'src/components/Preferences/usePreferences';
import { ResidentSubscription } from 'src/service/engageFrontendGateway';
import { countryToDataRegion } from 'src/utils/dataRegion';

// 'anonymous' — logged-out resident: email capture form.
// 'optIn' — authenticated resident: opt-in checkbox. May render pre-checked
// + locked when `isAlreadySubscribed` is true (already on every configured
// list); otherwise unchecked and interactive.
export type SignupStateKind = 'loading' | 'anonymous' | 'optIn';

export interface NotificationSignupState {
  kind: SignupStateKind;
  email?: string;
  missingPublicationIds: string[];
  isAlreadySubscribed: boolean;
}

// `configuredIds` are ResidentCustomPublication ids (resident_custom_publication_ids),
// so match on the subscription's `customPublicationId`, not its `entityId` (a page UUID).
function isSubscribedToAll(subscriptions: ResidentSubscription[], configuredIds: string[]): boolean {
  return configuredIds.every((id) =>
    subscriptions.some((subscription) => subscription.customPublicationId === id && subscription.isEnabled),
  );
}

function findMissingPublicationIds(subscriptions: ResidentSubscription[], configuredIds: string[]): string[] {
  return configuredIds.filter(
    (id) => !subscriptions.some((subscription) => subscription.customPublicationId === id && subscription.isEnabled),
  );
}

export function useNotificationSignupState(configuredIds: string[]): NotificationSignupState {
  const { organization } = useContext(PageContext);
  const { isAuthenticated, isSessionLoading, isMock } = useResidentSession();
  const { user, isUserLoading } = useResidentSession();
  const region = countryToDataRegion(organization?.country);
  // Skip the subscriptions fetch when there's nothing specific to check.
  const { data: subscriptions, isLoading: areSubscriptionsLoading } = usePreferencesQuery({
    region,
    isMock,
    enabled: isAuthenticated && configuredIds.length > 0,
  });

  if (isSessionLoading) {
    return { kind: 'loading', missingPublicationIds: [], isAlreadySubscribed: false };
  }
  if (!isAuthenticated) {
    return { kind: 'anonymous', missingPublicationIds: [], isAlreadySubscribed: false };
  }

  // Authenticated + no specific lists: opt-in checkbox tied to the user's
  // known email. We can't determine "already subscribed" without configured
  // ids, so leave the box unchecked.
  if (configuredIds.length === 0) {
    if (isUserLoading) return { kind: 'loading', missingPublicationIds: [], isAlreadySubscribed: false };
    return { kind: 'optIn', email: user?.email, missingPublicationIds: [], isAlreadySubscribed: false };
  }

  if (isUserLoading || areSubscriptionsLoading || !subscriptions) {
    return { kind: 'loading', missingPublicationIds: [], isAlreadySubscribed: false };
  }
  if (isSubscribedToAll(subscriptions, configuredIds)) {
    return { kind: 'optIn', email: user?.email, missingPublicationIds: [], isAlreadySubscribed: true };
  }
  return {
    kind: 'optIn',
    email: user?.email,
    missingPublicationIds: findMissingPublicationIds(subscriptions, configuredIds),
    isAlreadySubscribed: false,
  };
}
